import { useLazyQuery } from '@apollo/client';
import { Button, Dialog, DialogProps, TableOrderByType } from '@elipssolution/harfang';
import { mdiCheckCircle } from '@mdi/js';
import Icon from '@mdi/react';
import { Checkbox, Typography, styled } from '@mui/material';
import clsx from 'clsx';
import getConfig from 'next/config';
import { useSession as useNextAuthSession } from 'next-auth/react';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DocumentItem, { DocumentType } from './DocumentItem';
import DocumentUploadButton from './DocumentUploadButton';
import TagAutocomplete from './TagAutocomplete';
import useFormat from '../../../../hooks/useFormat';
import { useSession } from '../../../../src/components/session/SessionContext';
import { PublicRuntimeConfigType } from '../../../../src/types/publicRuntimeConfig';
import { uploadFilesWithFormData } from '../../../../src/utils/file';
import { FETCH_ENABLED_TAGS, FetchEnabledTagsType } from '../../api/tag';
import { TagType } from '../../types/tag';

const StyledDialog = styled(({ className, ...props }: DialogProps) => (
	<Dialog {...props} classes={{ paper: className }} />
))(({ theme: { spacing } }) => ({
	'& > div:first-of-type': {
		display: 'flex',
		justifyContent: 'center',
		padding: 0,

		'.error': {
			padding: spacing(1),
		},
	},
}));

const DialogContent = styled('div')(({ theme: { spacing } }) => ({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	maxHeight: '100%',

	'&.hasList': {
		marginLeft: `-${spacing(1)} !important`,
	},
}));

const GlobalDocumentSelector = styled('div')(({ theme: { spacing } }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: spacing(0, 1.5),
}));

const DocumentList = styled('div')(({ theme: { spacing } }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: spacing(0.5),
	height: '100%',
	paddingTop: spacing(1),
	overflowY: 'auto',

	'&.isMobileFormat': {
		paddingBottom: spacing(1),
	},
}));

const GlobalDocumentTagSelector = styled('div')(({ theme: { spacing, palette } }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: spacing(2),
	padding: spacing(0, 1.5),
	marginTop: spacing(2),
	marginLeft: spacing(1),

	'& > div': {
		width: 200,
	},

	'&.isMobileFormat': {
		borderTop: `1px solid ${palette.divider}`,
		marginTop: 0,
		padding: spacing(1.5, 1.5, 0, 1.5),

		'& > div:first-of-type': {
			width: '100%',
		},
	},
}));

const SuccessWrapper = styled('div')(({ theme: { palette, shape, spacing } }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: spacing(1),
	color: palette.success.main,
	backgroundColor: `${palette.success.main}1A`,
	backdropFilter: 'blur(30px)',
	borderRadius: shape.borderRadius,
	position: 'absolute',
	inset: spacing(0, 1.5),
	pointerEvents: 'none',

	'& > div': {
		display: 'flex',
		alignItems: 'center',
		gap: spacing(1),
	},
}));

const convertFilesToDocuments = (files: File[]): DocumentType[] =>
	files.map((file) => ({
		file: new File([file], file.name.replace(/[\\/:*?"<>|]/g, '_'), { type: file.type }),
	}));

type DocumentUploadDialogProps = {
	maxFiles?: number;
	maxFileSize?: number;
	onClose: () => void;
	onUploadSuccess?: () => void;
	open: boolean;
	uploadedFiles: File[] | null;
};

const DocumentUploadDialog = ({
	maxFiles,
	maxFileSize,
	onClose,
	onUploadSuccess,
	open,
	uploadedFiles,
}: DocumentUploadDialogProps) => {
	const { publicRuntimeConfig } = getConfig() as PublicRuntimeConfigType;
	const { maxPayloadSize } = publicRuntimeConfig;

	const { isMobileFormat } = useFormat();
	const { data: sessionData } = useNextAuthSession();
	const { access_token } = sessionData ?? {};
	const { customerFile: sessionCustomerFile } = useSession();

	const [documents, setDocuments] = useState<DocumentType[]>([]);
	const [selectedDocuments, setSelectedDocuments] = useState<DocumentType[]>([]);
	const [globalTag, setGlobalTag] = useState<TagType>();
	const [error, setError] = useState<string>();
	const [isUploading, setIsUploading] = useState(false);
	const [hasSucceeded, setHasSucceeded] = useState(false);

	const isAllDocumentsSelected = documents?.length === selectedDocuments?.length;

	const isValid = documents.length > 0 && documents.every(({ tag }) => !!tag);

	const accessTokenRef = useRef(access_token);

	const [fetchEnabledTags] = useLazyQuery<FetchEnabledTagsType>(FETCH_ENABLED_TAGS);

	const tagsDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
			orderBy?: TableOrderByType<TagType>,
		): Promise<{
			count: number;
			items: TagType[];
		}> => {
			const { field, order } = orderBy || {};

			const { data, error: tagsError } = await fetchEnabledTags({
				variables: {
					...(orderBy && { orderBy: { field, order } }),
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (tagsError) {
				throw tagsError;
			}

			const {
				document_enabledTags: { count = 0, items = [] },
			} = data ?? {
				document_enabledTags: {},
			};

			return {
				count,
				items,
			};
		},
		[fetchEnabledTags],
	);

	const handleClose = () => {
		setDocuments([]);
		setSelectedDocuments([]);
		setGlobalTag(undefined);
		setError(undefined);
		setIsUploading(false);
		setHasSucceeded(false);

		onClose();
	};

	const addFiles = useCallback(
		({ target: { files: targetFiles } }: ChangeEvent<HTMLInputElement>) =>
			setDocuments((prevFiles) => {
				if (!targetFiles) return prevFiles;

				const uploadedDocuments = convertFilesToDocuments(Array.from(targetFiles));

				if (!prevFiles) return uploadedDocuments;

				return [
					...prevFiles,
					...uploadedDocuments.filter(
						({ file: uploadedFile }) => !prevFiles?.some(({ file: prevFile }) => prevFile.name === uploadedFile.name),
					),
				];
			}),
		[],
	);

	const removeFile = (file: File) => {
		setDocuments((prevFiles) => prevFiles.filter(({ file: prevFile }) => prevFile !== file));

		setSelectedDocuments((prevSelectedDocuments) =>
			prevSelectedDocuments.filter(({ file: prevFile }) => prevFile !== file),
		);
	};

	const handleDocumentSelection = useCallback(
		(file: File, selected: boolean) =>
			setSelectedDocuments((prevSelectedDocuments) => {
				const document = documents?.find(({ file: prevFile }) => prevFile === file);

				if (!document) return prevSelectedDocuments;

				return document && selected
					? [...prevSelectedDocuments, document]
					: prevSelectedDocuments.filter(({ file: prevFile }) => prevFile !== file);
			}),
		[documents],
	);

	const handleDocumentTagChange = ({ file, tag }: DocumentType) =>
		setDocuments((prevDocuments) =>
			prevDocuments.map((document) => (document.file === file ? { ...document, tag } : document)),
		);

	const handleGlobalSelection = useCallback(
		() => (isAllDocumentsSelected ? setSelectedDocuments([]) : setSelectedDocuments(documents)),
		[isAllDocumentsSelected, documents],
	);

	const applyGlobalTag = useCallback(() => {
		// Apply the globalTag to the selectedDocuments
		setDocuments((prevDocuments) =>
			prevDocuments.map((document) =>
				selectedDocuments.find(({ file }) => file === document.file)
					? { ...document, tag: globalTag ?? undefined }
					: document,
			),
		);

		setSelectedDocuments([]);

		setGlobalTag(undefined);
	}, [globalTag, selectedDocuments]);

	const sendDocuments = async () => {
		if (!accessTokenRef.current || !sessionCustomerFile?.id) return;
		const accessToken = accessTokenRef.current;

		setIsUploading(true);

		const documentsPerStorageUploadMapper = documents.reduce((acc, document) => {
			const { tag } = document;
			const { storageUpload } = tag ?? {};
			const { id: storageUploadId } = storageUpload ?? {};

			if (!storageUploadId) return acc;

			acc.set(storageUploadId, [...(acc.get(storageUploadId) ?? []), document]);

			return acc;
		}, new Map<string, DocumentType[]>());

		const documentsToUpload = Array.from(documentsPerStorageUploadMapper.values()).flat();

		const { batches } = documentsToUpload.reduce<{
			batches: { files: File[]; tagIds: TagType['id'][] }[];
			currentBatchSize: number;
		}>(
			(acc, { file, tag }) => {
				const tagId = tag?.id;
				const fileSize = file.size;

				const lastBatch = acc.batches[acc.batches.length - 1];

				if (!lastBatch || acc.currentBatchSize + fileSize > Number(maxPayloadSize)) {
					acc.batches.push({
						files: [file],
						tagIds: tagId ? [tagId] : [],
					});
					acc.currentBatchSize = fileSize;
				} else {
					lastBatch.files.push(file);
					if (tagId) lastBatch.tagIds.push(tagId);
					acc.currentBatchSize += fileSize;
				}

				return acc;
			},
			{ batches: [], currentBatchSize: 0 },
		);

		await batches.reduce(async (prevPromise, batch) => {
			await prevPromise;

			const uri = new URL(`/document-uploads`, window.location.href);
			uri.searchParams.append('tagIds', JSON.stringify(batch.tagIds));

			await uploadFilesWithFormData({
				accessToken,
				selectedCustomerFileId: sessionCustomerFile.id,
				files: batch.files,
				uri,
			});
		}, Promise.resolve());

		setHasSucceeded(true);
		setIsUploading(false);

		onUploadSuccess?.();
	};

	const documentList = useMemo(
		() =>
			documents.map((document) => {
				const { file } = document;

				return (
					<DocumentItem
						document={document}
						isSelected={selectedDocuments?.includes(document)}
						key={file.name}
						onDelete={removeFile}
						onSelection={handleDocumentSelection}
						onTagChange={handleDocumentTagChange}
						tagsDataSource={tagsDataSource}
						withSelection={documents.length > 1}
					/>
				);
			}),
		[documents, handleDocumentSelection, selectedDocuments, tagsDataSource],
	);

	const actionsDialog: DialogProps['actionsDialog'] = hasSucceeded
		? [
				{
					id: 'close',
					label: 'Fermer',
					onClick: handleClose,
				},
		  ]
		: [
				{ id: 'cancel', label: 'Annuler', onClick: handleClose },
				{
					id: 'upload',
					disabled: !isValid,
					label: 'Déposer',
					onClick: sendDocuments,
					variant: 'contained',
				},
		  ];

	const extraInfos = useMemo(
		(): DialogProps['extraInfos'] =>
			!(hasSucceeded || error)
				? [
						<DocumentUploadButton
							key="document-upload-button"
							disabled={isUploading}
							maxFiles={maxFiles}
							maxFileSize={maxFileSize}
							onChange={addFiles}
							uploadedFilesLength={documents?.length}
						/>,
				  ]
				: [],
		[addFiles, documents, error, hasSucceeded, isUploading, maxFiles, maxFileSize],
	);

	useEffect(() => {
		uploadedFiles && setDocuments(convertFilesToDocuments(Array.from(uploadedFiles)));
	}, [uploadedFiles]);

	useEffect(() => {
		if (accessTokenRef.current !== access_token) {
			accessTokenRef.current = access_token;
		}
	}, [access_token]);

	return (
		<StyledDialog
			actionsDialog={actionsDialog}
			className={clsx({ error })}
			error={error}
			extraInfos={extraInfos}
			onClose={handleClose}
			open={open}
			title="Déposer des documents"
			maxWidth="sm"
			fullWidth
		>
			<DialogContent className={clsx({ hasList: documents.length > 1 })}>
				{documents && (
					<>
						{documents.length > 1 && (
							<GlobalDocumentSelector>
								<Checkbox
									checked={isAllDocumentsSelected}
									indeterminate={selectedDocuments.length > 0 && !isAllDocumentsSelected}
									onChange={handleGlobalSelection}
								/>

								<Typography fontWeight={500}>Tout sélectionner</Typography>
							</GlobalDocumentSelector>
						)}

						<DocumentList className={clsx({ isMobileFormat })}>{documentList}</DocumentList>

						{documents.length > 1 && (
							<GlobalDocumentTagSelector className={clsx({ isMobileFormat })}>
								<TagAutocomplete onChange={setGlobalTag} dataSource={tagsDataSource} value={globalTag} />

								<Button
									disabled={selectedDocuments.length === 0 || !globalTag}
									onClick={applyGlobalTag}
									variant="contained"
								>
									{isMobileFormat ? 'Appliquer' : 'Appliquer à la sélection'}
								</Button>
							</GlobalDocumentTagSelector>
						)}
					</>
				)}

				{hasSucceeded && (
					<SuccessWrapper>
						<Icon path={mdiCheckCircle} size="32px" />

						<Typography variant="h5">Déposé</Typography>
					</SuccessWrapper>
				)}
			</DialogContent>
		</StyledDialog>
	);
};

export default DocumentUploadDialog;
